/* eslint-disable max-len */
import { Card } from "react-bootstrap";
import PropTypes from "prop-types";
import classNames from "classnames";
import { useMemo } from "react";

import EqqoIcon from "../../../icons/EqqoIcon";
import { emotionColorMap } from "../../helpers/charts";
import { emotionToFields } from "../helpers/reportHelpers";
import { useUser } from "../../UserContext";

const emotionStyles = ( iconSize = 109 ) => ({
	Anger: {
		color: emotionColorMap.Anger,
		description: <><span className="text-danger">Distressing</span> Emotion</>,
		icon: <EqqoIcon height={iconSize} icon="anger" width={iconSize} />
	},
	Anxiety: {
		color: emotionColorMap.Anxiety,
		description: <><span className="text-danger">Distressing</span> Emotion</>,
		icon: <EqqoIcon height={iconSize} icon="anxiety" width={iconSize} />
	},
	Fear: {
		color: emotionColorMap.Fear,
		description: <><span className="text-danger">Distressing</span> Emotion</>,
		icon: <EqqoIcon height={iconSize} icon="fear" width={iconSize} />
	},
	Joy: {
		color: emotionColorMap.Joy,
		description: <><span className="text-green">Pleasant</span> Emotion</>,
		icon: <EqqoIcon height={iconSize} icon="joy" width={iconSize} />
	},
	Love: {
		color: emotionColorMap.Love,
		description: <><span className="text-green">Pleasant</span> Emotion</>,
		icon: <EqqoIcon height={iconSize} icon="love" width={iconSize} />
	},
	Sadness: {
		color: emotionColorMap.Sadness,
		description: <><span className="text-danger">Distressing</span> Emotion</>,
		icon: <EqqoIcon height={iconSize} icon="sadness" width={iconSize} />
	},
	Shame: {
		color: emotionColorMap.Shame,
		description: <><span className="text-danger">Distressing</span> Emotion</>,
		icon: <EqqoIcon height={iconSize} icon="shame" width={iconSize} />
	}
});

const EmotionCard = ({ className, emotion, horizontal, most, percentile, score }) => {
	const { respondent } = useUser();

	const stats = useMemo(() => respondent?.weqProfile?.stats, [respondent]);
	const percentiles = useMemo(() => respondent?.weqProfile?.percentiles, [respondent]);

	const emotionScore = useMemo(() => score ? score : stats?.[`${emotionToFields[emotion]}_dist`], [score, stats]);
	const emotionPercentile = useMemo(() => percentile ? percentile : percentiles?.[`${emotionToFields[emotion]}_dist`], [percentile, percentiles]);

	const iconSize = horizontal ? 75 : 100;

	return (
		<Card className={classNames(`bg-white`, className,)}>
			<Card.Body className={classNames({ 'd-flex flex-row justify-content-between align-items-center p-2' : horizontal })}>
				<div className={classNames(`d-flex flex-${horizontal ? "row" : "column"} gap-2`, { 'align-items-center': horizontal, 'text-center': !horizontal })}>
					<div className="fs-24 d-flex justify-content-center">{emotionStyles(iconSize)[emotion].icon}</div>
					<div className={classNames("d-flex flex-column", { 'gap-1': horizontal })}>
						<h6 className={classNames( "text-capitalize d-flex gap-1 align-items-center mb-0", { 'justify-content-center': !horizontal } )}>
							<span className={`rounded-circle d-inline-block bg-${emotionStyles(iconSize)[emotion].color}`} height="10" style={{ height: "10px", width: "10px" }} width="10" />
							{emotion}
						</h6>
						<div className="fs-14">{most ? 'Your Most ': ''}{emotionStyles(iconSize)[emotion].description}</div>
					</div>
				</div>
				{horizontal ? null : <hr />}
				<div className={classNames("d-flex justify-content-center text-center", { 'gap-3' : !horizontal })}>
					{
						typeof emotionScore === "undefined"
							? null
							: (
								<div className={classNames("score-container", { 'border-end pe-2': horizontal })}>
									<div className="fw-bold">
										{emotionScore?.toFixed(2)}%
									</div>
									<div className="text-secondary fs-14">
										Score
									</div>
								</div>
							)
					}
					{
						typeof emotionPercentile === "undefined"
							? null
							: (
								<div className={classNames("score-container", { 'border-start ps-2': horizontal })}>
									<div className="fw-bold">
										{emotionPercentile?.toFixed(2)}
									</div>
									<div className="text-secondary fs-14">
										Percentile
									</div>
								</div>
							)
					}
				</div>
			</Card.Body>
		</Card>
	);
}

EmotionCard.propTypes = {
	className: PropTypes.string,
	emotion: PropTypes.oneOf(["Joy", "Anger", "Anxiety", "Fear", "Sadness", "Love", "Shame"]).isRequired,
	horizontal: PropTypes.bool,
	most: PropTypes.bool,
	percentile: PropTypes.number,
	score: PropTypes.number
};

EmotionCard.defaultProps = {
	className: "",
	horizontal: false,
	most: false,
	percentile: null,
	score: null
};

export default EmotionCard;
